import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Heart, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const PRESETS = [10, 25, 50, 100, 250, 500];

const DESIGNATIONS = [
  { key: "general", label: "General Kingdom Fund" },
  { key: "love_thy_elder", label: "Love Thy Elder" },
  { key: "confidence_arise", label: "Confidence Arise" },
  { key: "community_programs", label: "Community Programs" },
  { key: "emergency_needs", label: "Emergency Needs" },
];

export default function Donate() {
  const [amount, setAmount] = useState(25);
  const [custom, setCustom] = useState("");
  const [designation, setDesignation] = useState("general");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    const value = parseFloat(custom || amount);
    if (!value || value < 1) { toast.error("Enter an amount of at least $1"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/donate/checkout", {
        amount: value,
        origin_url: window.location.origin,
        designation,
      });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start donation. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <Seo
        title="Give to the Kingdom · Jesus Just Us"
        description="Sow into the work. Your gift keeps Jesus Just Us free for every member, funds emergency help, supports elders and orphans, and underwrites community programs."
        path="/donate"
      />
      <div className="text-center">
        <div className="overline text-burgundy">Give to the work</div>
        <h1 className="font-serif text-5xl text-jjblue mt-3">Sow into the Kingdom</h1>
        <p className="mt-5 text-[var(--jj-text-secondary)] max-w-2xl mx-auto">
          Your gift keeps Jesus Just Us free for every member, funds emergency help, supports elders
          and orphans, and underwrites community programs.
        </p>
      </div>

      <div className="jj-card p-7 mt-10">
        <div className="overline text-burgundy">Choose an amount</div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
          {PRESETS.map((p) => (
            <button key={p} onClick={() => { setAmount(p); setCustom(""); }}
              data-testid={`donate-amount-${p}`}
              className={`py-3 rounded-xl border font-medium transition-all ${
                amount === p && !custom ? "bg-[var(--jj-burgundy)] text-cream border-[var(--jj-burgundy)]" : "bg-cream-surface text-jjblue border-jj hover:border-[var(--jj-gold)]"
              }`}>
              ${p}
            </button>
          ))}
        </div>
        <div className="mt-4">
          <label className="text-sm text-jjblue/80">Or enter another amount</label>
          <div className="flex items-center mt-1 max-w-[200px]">
            <span className="text-jjblue px-3 py-2.5 bg-cream-surface border border-jj border-r-0 rounded-l-xl">$</span>
            <input type="number" min="1" step="1" value={custom} onChange={(e) => setCustom(e.target.value)}
              placeholder="0.00" className="input-jj !rounded-l-none" data-testid="donate-custom" />
          </div>
        </div>

        <div className="mt-7">
          <div className="overline text-burgundy">Direct your gift</div>
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            {DESIGNATIONS.map((d) => (
              <button key={d.key} onClick={() => setDesignation(d.key)}
                data-testid={`donate-dest-${d.key}`}
                className={`text-left p-3 rounded-xl border transition-all ${
                  designation === d.key ? "bg-[var(--jj-burgundy)] text-cream border-[var(--jj-burgundy)]" : "bg-cream-surface border-jj hover:border-[var(--jj-gold)]"
                }`}>
                <div className="text-sm font-medium">{d.label}</div>
              </button>
            ))}
          </div>
        </div>

        <button onClick={start} disabled={busy} className="btn-primary w-full mt-8 flex items-center justify-center gap-2" data-testid="donate-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" strokeWidth={1.7} />}
          {busy ? "Redirecting…" : `Give $${custom || amount} now`}
        </button>
        <p className="mt-3 text-xs text-[var(--jj-muted)] text-center">
          Secure checkout via Stripe · You'll receive an email receipt.
        </p>
      </div>
    </div>
  );
}

export function DonationThanks() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setStatus("ok"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (attempts >= 5) { setStatus("pending"); return; }
        setTimeout(poll, 2000);
      } catch { setStatus("ok"); }
    };
    poll();
  }, [params]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <Seo title="Thank you for sowing · Jesus Just Us" path="/donate/thanks" noindex />
      <div className="jj-card p-10 fade-up">
        <CheckCircle2 className="h-12 w-12 text-burgundy mx-auto" strokeWidth={1.4} />
        <h1 className="font-serif text-4xl text-jjblue mt-5">Thank you for sowing</h1>
        <p className="mt-4 text-[var(--jj-text-secondary)]">
          {status === "paid" ? "Your gift has been received. A receipt is on its way to your inbox." :
            "Your donation is being processed. A receipt will arrive in your inbox shortly."}
        </p>
        <Link to="/" className="btn-primary mt-7 inline-block" data-testid="donate-home">Back to home</Link>
      </div>
    </div>
  );
}
